import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../services/firebase';
import { CheckCircle, XCircle, Clock, TrendingUp, Filter, Calendar } from 'lucide-react';

const statusStyles = {
  correct: { 
    text: 'Won', 
    color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    icon: <CheckCircle size={16} className="mr-1.5 text-green-600 dark:text-green-400" />
  },
  wrong: { 
    text: 'Lost', 
    color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    icon: <XCircle size={16} className="mr-1.5 text-red-600 dark:text-red-400" />
  },
  pending: { 
    text: 'Pending', 
    color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
    icon: <Clock size={16} className="mr-1.5 text-yellow-600 dark:text-yellow-400" />
  },
};

const predictionTypeLabels = {
  winner: 'Match Winner',
  first_inning_score: 'First Innings Score',
  over_runs: 'Runs in an Over',
  over_wickets: 'Wickets in an Over',
  match_winner: 'Match Winner',
  score_range: 'Score Range'
};

export default function PredictionHistory() {
  const [predictions, setPredictions] = useState([]);
  const [filter, setFilter] = useState('All');
  const [loading, setLoading] = useState(true);
  const [expandedPrediction, setExpandedPrediction] = useState(null);

  useEffect(() => {
    const fetchPredictions = async () => {
      try {
        setLoading(true);
        const user = auth.currentUser;
        if (!user) {
          console.warn('No user is logged in');
          setLoading(false);
          return;
        }

        const q = query(collection(db, 'predictions'), where('userId', '==', user.uid));
        const querySnapshot = await getDocs(q);

        const data = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : new Date()
        }));

        setPredictions(data);
      } catch (error) {
        console.error('Error fetching predictions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPredictions();
  }, []);

  const filteredPredictions =
    filter === 'All'
      ? predictions
      : predictions.filter((p) => p.type === filter);

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleExpand = (id) => {
    if (expandedPrediction === id) {
      setExpandedPrediction(null);
    } else {
      setExpandedPrediction(id);
    }
  };

  const renderPredictionValue = (prediction) => {
    switch (prediction.type) {
      case 'winner':
      case 'match_winner':
        return prediction.value;
      case 'first_inning_score':
      case 'score_range':
        return `${prediction.value} runs`;
      case 'over_runs':
        return `${prediction.value} runs in over ${prediction.overNumber + 1}`;
      case 'over_wickets':
        return `${prediction.value} wickets in over ${prediction.overNumber + 1}`;
      default:
        return prediction.value;
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md">
        <div className="flex items-center justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h2 className="text-xl font-bold dark:text-white">Prediction History</h2>
        
        <div className="relative">
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
            <Filter size={16} className="text-gray-400 dark:text-gray-500" />
          </div>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="pl-10 pr-10 py-2 bg-gray-50 dark:bg-gray-700 border-0 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 appearance-none text-gray-700 dark:text-gray-200"
          >
            <option value="All">All Predictions</option>
            <option value="winner">Match Winner</option>
            <option value="first_inning_score">First Innings Score</option>
            <option value="over_runs">Over Runs</option>
            <option value="over_wickets">Over Wickets</option>
          </select>
          <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
            <svg className="w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {filteredPredictions.length === 0 ? (
        <div className="text-center py-10 bg-gray-50 dark:bg-gray-700/30 rounded-xl">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 mb-4">
            <Calendar size={24} className="text-gray-500 dark:text-gray-400" />
          </div>
          <p className="text-gray-600 dark:text-gray-300">No predictions found.</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Make your first prediction to see it here!
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredPredictions.map((prediction) => {
            const status = statusStyles[prediction.status] || {
              text: prediction.status,
              color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
              icon: <Clock size={16} className="mr-1.5 text-gray-500" />
            };
            
            const isExpanded = expandedPrediction === prediction.id;

            return (
              <div 
                key={prediction.id} 
                className={`bg-gray-50 dark:bg-gray-700/50 rounded-xl overflow-hidden transition-all ${
                  isExpanded ? 'shadow-md' : ''
                }`}
              >
                <div 
                  className="p-4 cursor-pointer flex items-center justify-between"
                  onClick={() => toggleExpand(prediction.id)}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-2 h-10 rounded-full ${
                      prediction.status === 'correct' ? 'bg-green-500' : 
                      prediction.status === 'wrong' ? 'bg-red-500' : 
                      'bg-yellow-500'
                    }`}></div>
                    <div>
                      <p className="font-medium dark:text-white">
                        {predictionTypeLabels[prediction.type] || prediction.type.replace(/_/g, ' ')}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {renderPredictionValue(prediction)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className="text-right mr-4">
                      <div className="text-sm font-medium dark:text-white">{prediction.betAmount} coins</div>
                      <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                        <Calendar size={12} className="mr-1" />
                        {formatDate(prediction.createdAt)}
                      </div>
                    </div>
                    <div className={`flex items-center px-3 py-1 rounded-full text-xs font-medium ${status.color}`}>
                      {status.icon}
                      {status.text}
                    </div>
                  </div>
                </div>
                
                {isExpanded && (
                  <div className="px-4 pb-4 pt-1 border-t border-gray-200 dark:border-gray-600">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Match ID</p>
                        <p className="text-sm font-medium dark:text-white">{prediction.matchId}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Potential Winning</p>
                        <p className="text-sm font-medium flex items-center dark:text-white">
                          <TrendingUp size={14} className="mr-1 text-green-500" />
                          {prediction.potentialWinning || prediction.betAmount * 3} coins
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {filteredPredictions.length > 0 && (
        <div className="mt-6 text-center">
          <button className="text-blue-600 dark:text-blue-400 hover:underline text-sm font-medium">
            View All Predictions
          </button>
        </div>
      )}
    </div>
  );
}
